import React, { useState, useEffect, useMemo } from 'react';
import { parse } from 'csv-parse/browser/esm/sync';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender
} from '@tanstack/react-table';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import '../Media.css';

// Fix Leaflet's default icon issue
let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

// Add this new icon definition
let SelectedIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [30, 45], // Slightly larger for the selected marker
  iconAnchor: [15, 45],
  className: 'selected-marker'
});

L.Marker.prototype.options.icon = DefaultIcon;

// Component to fly to a marker when selected
function FlyToMarker({ marker }) {
  const map = useMap();
  
  useEffect(() => {
    if (marker && marker.Latitude && marker.Longitude) {
      map.flyTo(
        [parseFloat(marker.Latitude), parseFloat(marker.Longitude)],
        15,
        { duration: 1.5 }
      );
    }
  }, [map, marker]);
  
  return null;
}

function MarkerModal({ marker, onClose }) {
  if (!marker) return null;

  const fields = [
    { label: 'Marker Number', key: 'Marker_No' },
    { label: 'Title', key: 'Title' },
    { label: 'Subtitle', key: 'Subtitle' },
    { label: 'Description', key: 'Marker_Description' },
    { label: 'Address', key: 'Address' },
    { label: 'City', key: 'City' },
    { label: 'Zip', key: 'Zip' },
    { label: 'Marker Type', key: 'Marker_Type' },
    { label: 'Erected By', key: 'Erected_By' },
    { label: 'Time Period', key: 'Time_Period' },
    { label: 'Year Erected', key: 'Year_Erected' },
    { label: 'Location Notes', key: 'Location_Notes' },
    { label: 'Notes', key: 'Notes' },
    { label: 'Errata', key: 'Errata' },
    { label: 'Latitude', key: 'Latitude' },
    { label: 'Longitude', key: 'Longitude' },
    { label: 'Section/Quarter', key: 'Section_or_Quarter' },
    { label: 'County/Parish', key: 'County_or_Parish' },
    { label: 'Link', key: 'Link' },
    { label: 'First Published', key: 'First_Published' },
    { label: 'Last Update', key: 'Last_Update' }
  ];

  return (
    <div className="marker-modal-overlay" onClick={onClose}>
      <div className="marker-modal-content" onClick={e => e.stopPropagation()}>
        <button className="marker-modal-close" onClick={onClose}>×</button>
        <h2>{marker.Title}</h2>
        {marker.Subtitle && <h3 className="marker-subtitle">{marker.Subtitle}</h3>}
        <div className="marker-modal-body">
          {fields.map(field => (
            marker[field.key] && (
              <div key={field.key} className="marker-detail-row">
                <strong>{field.label}</strong>
                {field.key === 'Link' ? (
                  <a href={marker[field.key]} target="_blank" rel="noopener noreferrer" className="marker-link">
                    View External Link
                  </a>
                ) : (
                  <span>{marker[field.key]}</span>
                )}
              </div>
            )
          ))}
        </div>
        {marker.Latitude && marker.Longitude && (
          <div className="marker-actions">
            <a 
              href={`https://www.google.com/maps/search/?api=1&query=${marker.Latitude},${marker.Longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="button-outline"
            >
              View on Google Maps
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function MapView({ markers, onMarkerClick, searchTerm, selectedMarker, filters }) {
  const fields = [
    { label: 'Marker Number', key: 'Marker_No' },
    { label: 'Title', key: 'Title' },
    { label: 'Subtitle', key: 'Subtitle' },
    { label: 'Description', key: 'Marker_Description' },
    { label: 'Address', key: 'Address' },
    { label: 'City', key: 'City' },
    { label: 'Zip', key: 'Zip' },
    { label: 'Marker Type', key: 'Marker_Type' },
    { label: 'Erected By', key: 'Erected_By' },
    { label: 'Time Period', key: 'Time_Period' },
    { label: 'Year Erected', key: 'Year_Erected' }
  ];

  const validMarkers = markers.filter(marker => 
    marker.Title &&
    marker.Latitude && 
    marker.Longitude && 
    !isNaN(parseFloat(marker.Latitude)) && 
    !isNaN(parseFloat(marker.Longitude))
  );

  // Apply all filters
  const filteredMarkers = validMarkers.filter(marker => {
    // Search term filter
    const matchesSearch = !searchTerm || Object.values(marker)
      .some(value => value && value.toString().toLowerCase().includes(searchTerm.toLowerCase()));
    
    // City filter
    const matchesCity = !filters.city || marker.City === filters.city;
    
    // Year filter - Compare without decimal points
    const markerYear = marker.Year_Erected ? marker.Year_Erected.toString().split('.')[0] : '';
    const matchesYear = !filters.year || markerYear === filters.year;
    
    // Marker type filter
    const matchesType = !filters.markerType || marker.Marker_Type === filters.markerType;
    
    return matchesSearch && matchesCity && matchesYear && matchesType;
  });

  // Marker cluster group options
  const markerClusterOptions = {
    showCoverageOnHover: false,
    maxClusterRadius: 40,
    spiderfyOnMaxZoom: true
  };

  return (
    <div className="map-container">
      <div className="map-view">
        <MapContainer 
          center={[29.7604, -95.3698]} 
          zoom={11} 
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          
          {selectedMarker && (
            <FlyToMarker marker={selectedMarker} />
          )}
          
          <MarkerClusterGroup options={markerClusterOptions}>
            {filteredMarkers.map(marker => (
              <Marker
                key={marker.Title}
                position={[parseFloat(marker.Latitude), parseFloat(marker.Longitude)]}
                icon={selectedMarker?.Title === marker.Title ? SelectedIcon : DefaultIcon}
                eventHandlers={{
                  click: () => onMarkerClick(marker),
                }}
              />
            ))}
          </MarkerClusterGroup>
        </MapContainer>
      </div>
      <div className="map-sidebar">
        {selectedMarker ? (
          <>
            <h2>{selectedMarker.Title}</h2>
            {selectedMarker.Subtitle && <h3 className="marker-subtitle">{selectedMarker.Subtitle}</h3>}
            <div className="marker-details">
              {fields.map(field => selectedMarker[field.key] && (
                <div key={field.key} className="marker-detail-row">
                  <strong>{field.label}</strong>
                  <span>{selectedMarker[field.key]}</span>
                </div>
              ))}
              {selectedMarker.Marker_Description && (
                <div className="marker-description">
                  <strong>Description</strong>
                  <p>{selectedMarker.Marker_Description}</p>
                </div>
              )}
              {selectedMarker.Latitude && selectedMarker.Longitude && (
                <div className="marker-actions">
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${selectedMarker.Latitude},${selectedMarker.Longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="button-outline"
                  >
                    View on Google Maps
                  </a>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="empty-state">
            <p>Select a marker on the map to view details</p>
            <p className="marker-count">{filteredMarkers.length} markers displayed</p>
          </div>
        )}
      </div>
    </div>
  );
}

function FilterPanel({ data, filters, setFilters }) {
  // Extract unique values for filter options
  const cities = useMemo(() => {
    const uniqueCities = [...new Set(data.map(item => item.City).filter(Boolean))];
    return uniqueCities.sort();
  }, [data]);

  const years = useMemo(() => {
    // Get unique years and format them to remove decimal points
    const uniqueYears = [...new Set(data.map(item => {
      // Extract year value and remove any decimal points
      if (item.Year_Erected) {
        const yearValue = item.Year_Erected.toString().split('.')[0];
        return yearValue;
      }
      return null;
    }).filter(Boolean))];
    
    return uniqueYears.sort((a, b) => parseInt(a) - parseInt(b));
  }, [data]);

  const markerTypes = useMemo(() => {
    const uniqueTypes = [...new Set(data.map(item => item.Marker_Type).filter(Boolean))];
    return uniqueTypes.sort();
  }, [data]);

  const handleReset = () => {
    setFilters({
      city: '',
      year: '',
      markerType: ''
    });
  };

  return (
    <div className="filter-panel">
      <h3>Filter Markers</h3>
      <div className="filter-controls">
        <div className="filter-group">
          <label htmlFor="city-filter">City</label>
          <select
            id="city-filter"
            value={filters.city}
            onChange={(e) => setFilters({...filters, city: e.target.value})}
          >
            <option value="">All Cities</option>
            {cities.map(city => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>
        </div>
        
        <div className="filter-group">
          <label htmlFor="year-filter">Year Erected</label>
          <select
            id="year-filter"
            value={filters.year}
            onChange={(e) => setFilters({...filters, year: e.target.value})}
          >
            <option value="">All Years</option>
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
        
        <div className="filter-group">
          <label htmlFor="type-filter">Marker Type</label>
          <select
            id="type-filter"
            value={filters.markerType}
            onChange={(e) => setFilters({...filters, markerType: e.target.value})}
          >
            <option value="">All Types</option>
            {markerTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        
        <button className="filter-reset" onClick={handleReset}>
          Reset Filters
        </button>
      </div>
    </div>
  );
}

function Markers() {
  const [data, setData] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [filters, setFilters] = useState({
    city: '',
    year: '',
    markerType: ''
  });
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  useEffect(() => {
    fetch('/markers/merged_markers.csv')
      .then(response => response.text())
      .then(csvText => {
        const records = parse(csvText, {
          columns: true,
          skip_empty_lines: true,
          trim: true
        });
        setData(records);
      });
  }, []);

  // Apply filters to table data
  const filteredData = useMemo(() => {
    return data.filter(marker => {
      // City filter
      const matchesCity = !filters.city || marker.City === filters.city;
      
      // Year filter - Compare without decimal points
      const markerYear = marker.Year_Erected ? marker.Year_Erected.toString().split('.')[0] : '';
      const matchesYear = !filters.year || markerYear === filters.year;
      
      // Marker type filter
      const matchesType = !filters.markerType || marker.Marker_Type === filters.markerType;
      
      return matchesCity && matchesYear && matchesType;
    });
  }, [data, filters]);

  const columns = useMemo(() => [
    {
      header: 'Marker No.',
      accessorKey: 'Marker_No'
    },
    {
      header: 'Title',
      accessorKey: 'Title'
    },
    {
      header: 'City',
      accessorKey: 'City'
    },
    {
      header: 'Address',
      accessorKey: 'Address'
    }
  ], []);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      globalFilter
    },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel()
  });

  const handleRowClick = (row) => {
    setSelectedMarker(row.original);
  };

  const onMarkerClick = (marker) => {
    setSelectedMarker(marker);
  };

  const toggleFilterPanel = () => {
    setIsFilterPanelOpen(!isFilterPanelOpen);
  };

  return (
    <div className="media">
      <section className="page-hero">
        <h1>Historical Marker Inventory</h1>
        <p>Explore Harris County's historical markers and their stories</p>
      </section>

      <div className="page-container">
        <div className={`markers-controls ${viewMode === 'map' ? 'map-mode' : ''}`}>
          <div className="view-toggle">
            <button 
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
            >
              List View
            </button>
            <button 
              className={viewMode === 'map' ? 'active' : ''}
              onClick={() => setViewMode('map')}
            >
              Map View
            </button>
          </div>
          
          <div className="search-filter-container">
            <input
              type="text"
              value={globalFilter ?? ''}
              onChange={e => setGlobalFilter(e.target.value)}
              placeholder="Search markers..."
              className="markers-search"
            />
            <button 
              className={`filter-toggle ${isFilterPanelOpen ? 'active' : ''}`}
              onClick={toggleFilterPanel}
            >
              {isFilterPanelOpen ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>
        </div>

        {isFilterPanelOpen && (
          <FilterPanel 
            data={data}
            filters={filters}
            setFilters={setFilters}
          />
        )}

        {viewMode === 'list' ? (
          <>
            <div className="markers-stats">
              <p>Showing {table.getRowModel().rows.length} of {data.length} markers</p>
            </div>
            
            <div className="markers-table-container">
              <table className="markers-table">
                <thead>
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th 
                          key={header.id}
                          onClick={header.column.getToggleSortingHandler()}
                          className={header.column.getCanSort() ? 'sortable-header' : ''}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {header.column.getCanSort() && (
                            <span className="sort-indicator">
                              {header.column.getIsSorted() === 'asc' ? ' ↑' : 
                               header.column.getIsSorted() === 'desc' ? ' ↓' : ' ↕'}
                            </span>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map(row => (
                    <tr 
                      key={row.id}
                      onClick={() => handleRowClick(row)}
                      className="clickable-row"
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="markers-pagination">
              <button
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
              >
                {'<<'}
              </button>
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                {'<'}
              </button>
              <span>
                Page {table.getState().pagination.pageIndex + 1} of{' '}
                {table.getPageCount()}
              </span>
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                {'>'}
              </button>
              <button
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
              >
                {'>>'}
              </button>
              
              <select
                value={table.getState().pagination.pageSize}
                onChange={e => {
                  table.setPageSize(Number(e.target.value));
                }}
                className="page-size-select"
              >
                {[10, 25, 50, 100].map(pageSize => (
                  <option key={pageSize} value={pageSize}>
                    Show {pageSize}
                  </option>
                ))}
              </select>
            </div>
          </>
        ) : (
          <MapView 
            markers={data}
            selectedMarker={selectedMarker}
            onMarkerClick={onMarkerClick}
            searchTerm={globalFilter}
            filters={filters}
          />
        )}

        {viewMode === 'list' && selectedMarker && (
          <MarkerModal 
            marker={selectedMarker} 
            onClose={() => setSelectedMarker(null)} 
          />
        )}
      </div>
    </div>
  );
}

export default Markers; 